import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
var _excluded = ["className", "items", "minSwipeRequired", "numberOfItemsPerSwipe"];
import React, { useState, useRef } from 'react';
var styles = {
  "swiper-container": "pbui-cOkhl",
  "chevron-left-container": "pbui-kbpBV",
  "chevron-right-container": "pbui-bjc-b",
  "chevron-wrapper": "pbui-FU-vv",
  "chevron-icon": "pbui-N3i9O",
  "swiper-list": "pbui-DZg5B",
  "is-swiping": "pbui-J38ss",
  "swiper-indicator": "pbui-qhYf-",
  "swiper-indicator-item": "pbui-adHHb",
  "active": "pbui--Ty-g"
};
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faChevronRight } from '@fortawesome/pro-solid-svg-icons';
import clsx from 'clsx';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
function useStateRef(defaultValue) {
  var ref = useRef(defaultValue);
  var _useState = useState(defaultValue),
    _useState2 = _slicedToArray(_useState, 2),
    state = _useState2[0],
    _setState = _useState2[1];
  var setState = function setState(value) {
    _setState(value);
    ref.current = value;
  };
  return [state, setState, ref];
}
function getTouchEventData(e) {
  return 'changedTouches' in e ? e.changedTouches[0] : e;
}
function getRefValue(ref) {
  return ref.current;
}
var Swiper = /*#__PURE__*/React.forwardRef(function Swiper(props, ref) {
  var className = props.className,
    items = props.items,
    _props$minSwipeRequir = props.minSwipeRequired,
    minSwipeRequired = _props$minSwipeRequir === void 0 ? 50 : _props$minSwipeRequir,
    _props$numberOfItemsP = props.numberOfItemsPerSwipe,
    numberOfItemsPerSwipe = _props$numberOfItemsP === void 0 ? 1 : _props$numberOfItemsP,
    rest = _objectWithoutProperties(props, _excluded);
  var numberOfItems = items.length;
  var realNumberOfItems = Math.ceil(numberOfItems / numberOfItemsPerSwipe);
  var containerRef = useRef(null);
  var containerWidthRef = useRef(0);
  var minOffsetXRef = useRef(0);
  var currentOffsetXRef = useRef(0);
  var startXRef = useRef(0);
  var _useStateRef = useStateRef(0),
    _useStateRef2 = _slicedToArray(_useStateRef, 3),
    offsetX = _useStateRef2[0],
    setOffsetX = _useStateRef2[1],
    offsetXRef = _useStateRef2[2];
  var _useState3 = useState(false),
    _useState4 = _slicedToArray(_useState3, 2),
    isSwiping = _useState4[0],
    setIsSwiping = _useState4[1];
  var _useState5 = useState(0),
    _useState6 = _slicedToArray(_useState5, 2),
    currentIdx = _useState6[0],
    setCurrentIdx = _useState6[1];
  var onTouchMove = function onTouchMove(e) {
    var currentX = getTouchEventData(e).clientX;
    var diff = getRefValue(startXRef) - currentX;
    var newOffsetX = getRefValue(currentOffsetXRef) - diff;
    if (realNumberOfItems > 1) {
      setOffsetX(newOffsetX);
    }
  };
  var onTouchEnd = function onTouchEnd() {
    var currentOffsetX = getRefValue(currentOffsetXRef);
    var containerWidth = getRefValue(containerWidthRef);
    var newOffsetX = getRefValue(offsetXRef);
    var diff = currentOffsetX - newOffsetX;

    // we need to check difference in absolute/positive value (if diff is more than minSwipeRequired px)
    if (Math.abs(diff) > minSwipeRequired) {
      if (diff > 0) {
        if (currentIdx === realNumberOfItems - 1) {
          // last page to first
          setCurrentIdx(0);
          newOffsetX = 0;
        } else {
          // n-th page to n+1
          // swipe to the right if diff is positive
          newOffsetX = Math.floor(newOffsetX / containerWidth) * containerWidth;
          var newCurrentIndex = Math.abs(newOffsetX / containerWidth);
          setCurrentIdx(Math.abs(newOffsetX / containerWidth));
          if (newCurrentIndex === realNumberOfItems - 1 && numberOfItems % numberOfItemsPerSwipe !== 0) {
            // last page doesn't have full items 
            newOffsetX = newOffsetX + (numberOfItemsPerSwipe - numberOfItems % numberOfItemsPerSwipe) * containerWidth / numberOfItemsPerSwipe;
          }
        }
      } else {
        if (currentIdx === 0) {
          // first page to last
          setCurrentIdx(realNumberOfItems - 1);
          newOffsetX = -(realNumberOfItems - 1) * containerWidth;
          if (numberOfItems % numberOfItemsPerSwipe !== 0) {
            // last page doesn't have full items 
            newOffsetX = newOffsetX + (numberOfItemsPerSwipe - numberOfItems % numberOfItemsPerSwipe) * containerWidth / numberOfItemsPerSwipe;
          }
        } else {
          // n-th page to n-1
          // swipe to the left if diff is negative
          newOffsetX = Math.ceil(newOffsetX / containerWidth) * containerWidth;
          setCurrentIdx(Math.abs(newOffsetX / containerWidth));
        }
      }
    } else {
      // remain in the current image since diff was <= minSwipeRequired
      if (currentIdx === realNumberOfItems - 1 && numberOfItems % numberOfItemsPerSwipe !== 0) {
        // last page doesn't have full items and don't the page
        setCurrentIdx(realNumberOfItems - 1);
        newOffsetX = -(realNumberOfItems - 1) * containerWidth + (numberOfItemsPerSwipe - numberOfItems % numberOfItemsPerSwipe) * containerWidth / numberOfItemsPerSwipe;
      } else {
        // don't move the page
        newOffsetX = Math.round(newOffsetX / containerWidth) * containerWidth;
        setCurrentIdx(Math.abs(newOffsetX / containerWidth));
      }
    }
    setIsSwiping(false);
    setOffsetX(newOffsetX);
    window.removeEventListener('touchend', onTouchEnd);
    window.removeEventListener('touchmove', onTouchMove);
    window.removeEventListener('mouseup', onTouchEnd);
    window.removeEventListener('mousemove', onTouchMove);
  };
  var onTouchStart = function onTouchStart(e) {
    setIsSwiping(true);
    currentOffsetXRef.current = getRefValue(offsetXRef);
    startXRef.current = getTouchEventData(e).clientX;
    var containerEl = getRefValue(containerRef);
    var containerWidth = containerEl.offsetWidth;
    containerWidthRef.current = containerWidth;
    minOffsetXRef.current = containerWidth - containerEl.scrollWidth;
    window.addEventListener('touchmove', onTouchMove);
    window.addEventListener('touchend', onTouchEnd);
    window.addEventListener('mousemove', onTouchMove);
    window.addEventListener('mouseup', onTouchEnd);
  };
  var indicatorOnClick = function indicatorOnClick(idx) {
    var containerEl = getRefValue(containerRef);
    var containerWidth = containerEl.offsetWidth;
    setCurrentIdx(idx);
    if (idx === realNumberOfItems - 1 && numberOfItems % numberOfItemsPerSwipe !== 0) {
      var moveToFitPerfectlyToTheRight = (numberOfItemsPerSwipe - numberOfItems % numberOfItemsPerSwipe) * containerWidth / numberOfItemsPerSwipe;
      setOffsetX(-(containerWidth * idx) + moveToFitPerfectlyToTheRight);
    } else {
      setOffsetX(-(containerWidth * idx));
    }
  };
  var swiperContainerClasses = clsx(styles["swiper-container"], className);
  return /*#__PURE__*/_jsxs("div", {
    children: [/*#__PURE__*/_jsxs("div", _extends({
      className: swiperContainerClasses,
      onTouchStart: onTouchStart,
      onMouseDown: onTouchStart,
      ref: ref
    }, rest, {
      children: [realNumberOfItems > 1 && /*#__PURE__*/_jsx("div", {
        className: styles["chevron-left-container"],
        children: /*#__PURE__*/_jsx("div", {
          className: "group ".concat(styles["chevron-wrapper"]),
          onClick: function onClick() {
            return indicatorOnClick(currentIdx === 0 ? realNumberOfItems - 1 : currentIdx - 1);
          },
          role: "button",
          tabIndex: 0,
          "aria-label": "Previous slide",
          children: /*#__PURE__*/_jsx(FontAwesomeIcon, {
            icon: faChevronLeft,
            className: styles["chevron-icon"]
          })
        })
      }), /*#__PURE__*/_jsx("ul", {
        ref: containerRef,
        className: "".concat(styles["swiper-list"], " ").concat(isSwiping ? styles["is-swiping"] : ''),
        style: {
          transform: "translate3d(".concat(offsetX, "px, 0, 0)")
        },
        children: items.map(function (item, index) {
          return /*#__PURE__*/_jsx(React.Fragment, {
            children: /*#__PURE__*/React.cloneElement(item.children, {
              numberOfItemsPerSwipe: numberOfItemsPerSwipe
            })
          }, index);
        })
      }), realNumberOfItems > 1 && /*#__PURE__*/_jsx("div", {
        className: styles["chevron-right-container"],
        children: /*#__PURE__*/_jsx("div", {
          className: styles["chevron-wrapper"],
          onClick: function onClick() {
            return indicatorOnClick(currentIdx === realNumberOfItems - 1 ? 0 : currentIdx + 1);
          },
          role: "button",
          tabIndex: 0,
          "aria-label": "Next slide",
          children: /*#__PURE__*/_jsx(FontAwesomeIcon, {
            icon: faChevronRight,
            className: styles["chevron-icon"]
          })
        })
      })]
    })), /*#__PURE__*/_jsx("ul", {
      className: styles["swiper-indicator"],
      children: realNumberOfItems > 1 && Array.from(new Array(realNumberOfItems)).map(function (num, idx) {
        return /*#__PURE__*/_jsx("li", {
          className: "".concat(styles["swiper-indicator-item"], " ").concat(currentIdx === idx ? styles["active"] : ''),
          onClick: function onClick() {
            return indicatorOnClick(idx);
          },
          "data-testid": "indicator",
          role: "button",
          tabIndex: 0,
          "aria-label": "Slide ".concat(idx + 1, " of ").concat(realNumberOfItems)
        }, idx);
      })
    })]
  });
});
export default Swiper;